import React, { useRef, useState } from 'react';
import moment from 'moment';
import DatePicker from 'react-multi-date-picker';
import {
    Box,
    Text,
    Input,
    Button,
    Flex,
    Tooltip,
    InputGroup,
    InputRightElement,
    InputLeftElement,
    IconButton,
} from '@chakra-ui/react';
import { AddIcon, ArrowLeftIcon, ArrowRightIcon, ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';

import { CalendarIcon2, InfoOutlineIcon } from '@assets/icons';
import { shortMonthLists } from '@helpers/const';
import { MODE_CONSTRAINTS, RANGE_CONSTRAINTS } from './const';
import './style.css';
import OutsideClickHandler from 'react-outside-click-handler';

export const DatePicker2 = ({
    label = 'Date Range',
    placeholder = 'Pilih Tanggal',
    mode = MODE_CONSTRAINTS.RANGE,
    constraint = RANGE_CONSTRAINTS.FLEXIBLE,
    value,
    onChange,
    formatDisplayValue,
    width = '100%',
    isIconRight = false,
    isDimBg = false,
    tooltipLabel = false,
    maxDate,
    onlyMonthPicker = false,
    isDisabled = false,
    isInvalid = false,
    isRequired = false,
    extraStyle,
    isDisableSelectToday = false,
}) => {
    const [internalValues, setInternalValues] = useState([]);
    const dates = value ?? internalValues;
    const datePickerRef = useRef(null);

    let minDate = undefined;
    let customMaxDate = maxDate;

    if (mode === MODE_CONSTRAINTS.RANGE && constraint === RANGE_CONSTRAINTS.SAME_MONTH && dates?.length === 1) {
        const selected = dates[0];
        const year = selected.year;
        const month = selected.month.index;
        const endOfDay = [23, 59, 59, 999];

        minDate = new Date(year, month, 1);
        maxDate = new Date(year, month + 1, 0, ...endOfDay);

        const endOfMonth = new Date(year, month + 1, 0, ...endOfDay);
        customMaxDate = customMaxDate && customMaxDate < endOfMonth ? customMaxDate : endOfMonth;
    }

    const displayValue = React.useMemo(() => {
        if (formatDisplayValue) return formatDisplayValue(dates);
        if (!dates || dates.length === 0) return '';

        if (onlyMonthPicker) {
            return dates[0]?.format?.('MMM YYYY') || '';
        }

        if (mode === MODE_CONSTRAINTS.SINGLE) return dates[0]?.format?.('DD MMM YYYY') || '';

        if (mode === MODE_CONSTRAINTS.MULTIPLE) {
            if (dates.length <= 2) {
                return dates
                    .map((d) => d?.format?.('DD MMM YYYY'))
                    .filter(Boolean)
                    .join(', ');
            }
            return `${dates[0]?.format?.('DD MMM YYYY')}... (+${dates.length - 1} more)`;
        }
        if (dates.length === 1) return dates[0]?.format?.('DD MMM YYYY') || '';

        const d1 = dates[0];
        const d2 = dates[1];

        if (!d1 || !d2) return '';

        if (d1.year === d2.year && d1.month?.index === d2.month?.index) {
            return `${d1.format?.('DD')} - ${d2.format?.('DD MMM YYYY')}`;
        }
        if (d1.year === d2.year) {
            return `${d1.format?.('DD MMM')} - ${d2.format?.('DD MMM YYYY')}`;
        }
        return `${d1.format?.('DD MMM YYYY')} - ${d2.format?.('DD MMM YYYY')}`;
    }, [dates, formatDisplayValue, mode, onlyMonthPicker]);

    const handleChange = (newVal) => {
        const normalized = newVal ? (Array.isArray(newVal) ? newVal : [newVal]) : [];

        if (value === undefined) setInternalValues(normalized);
        if (onChange) onChange(normalized);

        if (mode === MODE_CONSTRAINTS.SINGLE && normalized.length === 1) {
            datePickerRef.current?.closeCalendar?.();
        } else if (mode === MODE_CONSTRAINTS.RANGE && normalized.length === 2) {
            datePickerRef.current?.closeCalendar?.();
        }
    };

    const handleCloseCalendar = () => datePickerRef.current?.closeCalendar();
    const handleSelectToday = () => {
        const today = moment();
        handleChange([today, today]);
        handleCloseCalendar();
    };

    const handleClearValue = () => {
        handleChange([]);
    };

    const bg = isDimBg && displayValue ? 'semantic.200' : 'semantic.100';

    return (
        <Box width={width} style={{ ...extraStyle }}>
            <OutsideClickHandler onOutsideClick={handleCloseCalendar}>
                <Flex gap="8px" alignItems="center" mb={2} justifyContent={'space-between'}>
                    <Text fontSize={'14px'} fontWeight={'700'}>
                        {label}{' '}
                        {isRequired && (
                            <Box as="span" color={'red'}>
                                *
                            </Box>
                        )}
                    </Text>
                    {tooltipLabel && (
                        <Tooltip hasArrow label={tooltipLabel} placement="top-end">
                            <Box>
                                <InfoOutlineIcon />
                            </Box>
                        </Tooltip>
                    )}
                </Flex>

                <DatePicker
                    ref={datePickerRef}
                    value={mode === MODE_CONSTRAINTS.SINGLE || onlyMonthPicker ? dates[0] : dates}
                    showOtherDays
                    sele
                    onChange={handleChange}
                    minDate={minDate}
                    maxDate={customMaxDate}
                    range={mode === MODE_CONSTRAINTS.RANGE}
                    rangeHover={mode === MODE_CONSTRAINTS.RANGE}
                    multiple={mode === MODE_CONSTRAINTS.MULTIPLE}
                    containerClassName="rmdp-container"
                    weekStartDayIndex={1}
                    highlightToday={false}
                    months={shortMonthLists}
                    onlyMonthPicker={onlyMonthPicker}
                    renderButton={(direction, handleClick, disabled) => (
                        <Box display="flex" gap={1} px={2} alignItems="center">
                            {direction === 'left' && (
                                <Button
                                    color={'semantic.500'}
                                    size="xs"
                                    variant="text"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        for (let i = 0; i < 12; i++) handleClick(e);
                                    }}
                                    disabled={isDisabled}
                                >
                                    <ArrowLeftIcon w={'10px'} h={'10px'} />
                                </Button>
                            )}
                            <Button
                                color={'semantic.500'}
                                size="xs"
                                variant="text"
                                onClick={(e) => handleClick(e)}
                                disabled={isDisabled}
                            >
                                {direction === 'left' ? (
                                    <ChevronLeftIcon w={'21px'} h={'21px'} />
                                ) : (
                                    <ChevronRightIcon w={'21px'} h={'21px'} />
                                )}
                            </Button>
                            {direction === 'right' && (
                                <Button
                                    color={'semantic.500'}
                                    size="xs"
                                    variant="text"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        for (let i = 0; i < 12; i++) handleClick(e);
                                    }}
                                    disabled={isDisabled}
                                >
                                    <ArrowRightIcon w={'10px'} h={'10px'} />
                                </Button>
                            )}
                        </Box>
                    )}
                    render={(_value, openCalendar) => (
                        <Flex align="center" gap={2} onClick={openCalendar}>
                            <InputGroup>
                                {isIconRight ? (
                                    <InputRightElement pointerEvents="none">
                                        <CalendarIcon2 />
                                    </InputRightElement>
                                ) : (
                                    <InputLeftElement pointerEvents="none">
                                        <CalendarIcon2 />
                                    </InputLeftElement>
                                )}
                                <Input
                                    value={displayValue}
                                    onClick={openCalendar}
                                    readOnly
                                    placeholder={placeholder}
                                    bg={bg}
                                    cursor="pointer"
                                    disabled={isDisabled}
                                    isInvalid={isInvalid}
                                    errorBorderColor={isInvalid}
                                />

                                <InputRightElement>
                                    <IconButton
                                        onClick={handleClearValue}
                                        variant={'unstyled'}
                                        color={'black'}
                                        size={'sm'}
                                        icon={<AddIcon transform={'rotate(45deg)'} />}
                                        disabled={isDisabled}
                                    />
                                </InputRightElement>
                            </InputGroup>
                        </Flex>
                    )}
                    plugins={[
                        <Box position="bottom" className="select-today">
                            {!isDisableSelectToday && (
                                <Button onClick={handleSelectToday} variant={'text'} width={'full'}>
                                    Today
                                </Button>
                            )}
                        </Box>,
                    ]}
                />
            </OutsideClickHandler>
        </Box>
    );
};
